import React from 'react';
import { Link } from 'react-router';

import LegalLayout, { LegalSection } from './LegalLayout';

const SUPPORT_EMAIL = 'support@snappytrade.app';

/**
 * Privacy Policy for Snappy Trade. Original text written for this product;
 * review by qualified counsel before public launch is strongly recommended.
 */
const Privacy: React.FC = () => (
    <LegalLayout
        title="Privacy Policy"
        effectiveDate="October 4, 2026"
        counterpart={{ label: 'Terms of Use →', to: '/terms' }}
    >
        <LegalSection title="1. Overview">
            <p>
                This Privacy Policy explains how the Snappy Trade team
                (&quot;Snappy Trade&quot;, &quot;we&quot;, &quot;us&quot;)
                collects, uses and shares information when you use the Snappy
                Trade website and application (the &quot;Service&quot;). It
                forms part of our{' '}
                <Link to="/terms" className="text-info hover:underline">
                    Terms of Use
                </Link>
                . The Service is self-custody software: your wallet key and
                recovery phrase stay in your browser and never reach us. You do
                not need to give us a name, phone number or postal address to
                use it.
            </p>
        </LegalSection>

        <LegalSection title="2. Information We Collect">
            <p>
                <strong>Wallet and blockchain data.</strong> The address of your
                Snappy Wallet. Your balances, deposits, withdrawals and trades
                are recorded on Hyperliquid and public blockchains, which are
                public by design and visible to anyone, independently of us.
                Your browser reads and sends them directly to those networks,
                not through our servers.
            </p>
            <p>
                <strong>Social sign-in data.</strong> When you sign in with
                Google, Discord, X or Telegram, we receive a limited profile
                from the provider: an account identifier, your display name or
                handle, and, depending on the provider, an email address. We do
                not receive your password.
            </p>
            <p>
                <strong>Your encrypted Snappy backup.</strong> If you back up
                with Snappy, your recovery phrase is encrypted in your browser
                with your password and a recovery key that only you hold, and we
                store the encrypted copy together with its encryption
                parameters, your wallet address, the time it was made and the
                identifiers of the sign-ins linked to your account. We never
                receive the password or the recovery key and cannot decrypt,
                recover or reset the backup. We keep it until you delete or
                replace it.
            </p>
            <p>
                <strong>Launch access.</strong> If the Service asks you for a
                launch code, we record that your sign-in account redeemed one,
                when, and the IP address it was redeemed from.
            </p>
            <p>
                <strong>Technical data.</strong> When your browser calls our
                servers (for sign-in, your backup, launch codes and public
                market data such as prices, market lists and the live trades
                tape), they receive standard request information such as your IP
                address, browser and device type and approximate region, and
                keep diagnostic logs needed to operate and secure these
                services. The Service contains no advertising trackers.
            </p>
            <p>
                <strong>Information kept only in your browser.</strong> Your
                wallet key stays in this browser&apos;s local storage, together
                with your interface preferences, your sign-in state and the
                records the interface needs to show your trades (such as the
                order ids of your own trades). See Section 5.
            </p>
            <p>
                We do not collect government identification, bank or card
                details, and we never ask you to send us your wallet&apos;s
                private key or recovery phrase; the only place you ever type a
                recovery phrase is in your own browser, when you import a
                wallet.
            </p>
        </LegalSection>

        <LegalSection title="3. How We Use Information">
            <ul>
                <li>
                    to operate the Service: sign you in, hold your encrypted
                    backup so that you can restore your wallet on another
                    browser, and recognise your account;
                </li>
                <li>
                    to secure the Service: detect and prevent fraud, abuse,
                    unauthorized access and violations of our Terms, including
                    geographic restrictions;
                </li>
                <li>
                    to improve the Service: diagnose errors and measure the
                    performance of our sign-in, backup and market-data services;
                </li>
                <li>
                    to communicate with you about the Service when you contact
                    us;
                </li>
                <li>to comply with applicable law and legal process.</li>
            </ul>
        </LegalSection>

        <LegalSection title="4. How We Share Information">
            <p>We do not sell personal information. We share it only:</p>
            <ul>
                <li>
                    with infrastructure providers that make the Service work
                    (hosting providers and the sign-in providers you choose),
                    each receiving only what their role requires;
                </li>
                <li>
                    with professional advisers, or in connection with a
                    reorganization, acquisition or transfer of the Service;
                </li>
                <li>
                    when required by law, regulation or legal process, or to
                    protect the rights, safety and property of users, the public
                    or Snappy Trade.
                </li>
            </ul>
            <p>
                Your browser itself sends your wallet address and the actions
                you sign directly to Hyperliquid, bridging and relaying
                providers and blockchain RPC providers, to show your balances
                and carry out what you ask. They handle that information under
                their own policies.
            </p>
        </LegalSection>

        <LegalSection title="5. Cookies and Local Storage">
            <p>
                The Service uses browser storage that is strictly necessary for
                it to function: your wallet key (until you sign out or forget
                the wallet), your sign-in state, your interface preferences
                (such as chart mode), the records the interface keeps about your
                own orders, and whether you accepted our terms. These live in
                your browser and can be cleared at any time through your browser
                settings. Clearing them signs you out and deletes the wallet key
                from this browser; it does not delete your Snappy backup and
                does not move your funds, which stay reachable with your
                recovery phrase or your backup. If we adopt analytics cookies in
                the future, we will update this policy first.
            </p>
        </LegalSection>

        <LegalSection title="6. Blockchain Data Is Public">
            <p>
                Once a transaction is recorded on a public blockchain it is
                permanent and visible to anyone, and neither you nor we can edit
                or delete it. Deletion rights under this policy therefore cannot
                extend to on-chain data.
            </p>
        </LegalSection>

        <LegalSection title="7. Security">
            <p>
                We use technical and organizational measures appropriate to the
                nature of the data we handle, including encrypted transport
                (HTTPS/TLS), scoped credentials and access controls. Your key
                never leaves your browser, and your Snappy backup is encrypted
                with your password and a recovery key that only you hold before
                it leaves it. No system is perfectly secure, and a browser is a
                shared environment: you are responsible for securing your own
                device and browser, recovery phrase, backup password, recovery
                key and social accounts, and for signing out on devices you do
                not control.
            </p>
        </LegalSection>

        <LegalSection title="8. Data Retention">
            <p>
                We keep sign-in records and your encrypted backup for as long as
                needed to provide the Service, comply with legal obligations,
                resolve disputes and enforce agreements; you can delete a backup
                at any time in the interface. We do not operate the systems that
                record your deposits, withdrawals and trades; those records live
                on Hyperliquid and public blockchains.
            </p>
        </LegalSection>

        <LegalSection title="9. Your Rights">
            <p>
                Subject to applicable law, you may request access to, correction
                of, or deletion of the personal information we hold about you by
                emailing <strong>{SUPPORT_EMAIL}</strong>. We may need to verify
                that a request genuinely comes from the holder of the relevant
                wallet or sign-in account, and some data cannot be deleted
                (Section 6, and records we must keep by law). Depending on where
                you live, you may also have rights to object to or restrict
                certain processing and to lodge a complaint with a supervisory
                authority.
            </p>
        </LegalSection>

        <LegalSection title="10. International Transfers">
            <p>
                We and our infrastructure providers operate globally, so your
                information may be processed in countries with data-protection
                rules different from those where you live. Where required, we
                take steps designed to ensure it remains protected in line with
                this policy.
            </p>
        </LegalSection>

        <LegalSection title="11. Children">
            <p>
                The Service is not directed at, and may not be used by, anyone
                under 18 years of age. We do not knowingly collect information
                from minors; if we learn that we have, we will delete it.
            </p>
        </LegalSection>

        <LegalSection title="12. Third-Party Links and Services">
            <p>
                The Service links to and depends on third-party services
                (including sign-in providers, Hyperliquid, bridging providers,
                blockchains, RPC providers and explorers). Their handling of
                your information is governed by their own privacy policies,
                which we encourage you to read.
            </p>
        </LegalSection>

        <LegalSection title="13. Changes to this Policy">
            <p>
                We may update this policy from time to time. The current
                version, with its effective date, will always be available at
                this page, and material changes may additionally be announced in
                the interface. Continued use of the Service after an update
                constitutes acceptance of the updated policy.
            </p>
        </LegalSection>

        <LegalSection title="14. Contact">
            <p>
                Privacy questions or requests: <strong>{SUPPORT_EMAIL}</strong>
            </p>
        </LegalSection>
    </LegalLayout>
);

export default Privacy;
